import i18next from 'i18next'; // Import the i18next instance
import { NOLEGGIATORI } from '../../../utils/constants';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import { Button, Form } from 'react-bootstrap';
import Icon from 'src/components/Icon/Icon';
import { MdAddCircle } from 'react-icons/md';
import { useAiContext } from 'src/context/AiContext';
import { computeArvalWithFad, computeFadInfo, quickoNoleggiatoreLabel } from 'src/utils/quicko';

export const columnsData = (
	downloadPdf: (row: any) => void,
	isSegnalatore?: boolean,
	type?: string,
	rowOverrides?: Record<string, any>,
	handleRowOverride?: (
		rowId: string,
		overrides?: Record<string, any> | null
	) => void,
	inZoom?: boolean
) => [
		{
			accessor: 'versione_locale',
			id: 'versione_locale',
			Header: i18next.t('car_setup'),
			width: 250,
		},
		{
			accessor: 'noleggiatore',
			id: 'noleggiatore',
			Header: i18next.t('rental_company'),
			width: 250,
			Cell: (data) => {
				const { quotReqState, setFadWarning } = useAiContext();
				const {
					optionalTotals,
					vehicleState: { dati_veicolo },
				} = quotReqState;

				const val = quickoNoleggiatoreLabel(data.value);

				if (type !== 'best_offers') {
					return <small>{val}</small>;
				}

				const handleFadChange = (checked?: boolean | null) => {
					if (!checked) {
						setFadWarning(null);
						handleRowOverride?.(data.row, null);
						return;
					}

					const { warning, overrides } = computeArvalWithFad({
						offer: data.row.original,
						prezzo_listino: dati_veicolo?.prezzo_listino,
						optionalTotals,
					});

					setFadWarning(warning ?? null);
					if (!!warning) return;

					handleRowOverride?.(data.row, overrides);
				};

				if (
					[NOLEGGIATORI.ARVAL_PRIVATI, NOLEGGIATORI.ARVAL_P_IVA].includes(val)
				) {
					const isOverridden = !!rowOverrides?.[data.row.id];
					return (
						<div>
							<small>{val}</small>
							<div className="d-flex align-items-center justify-content-between">
								<div className="text-cyan">
									<small>Applica sconto Horizon</small>
								</div>
								<Form.Check
									type="switch"
									checked={isOverridden}
									onChange={(e) => handleFadChange(e.target.checked)}
									className="hor-form-check-switch ms-2"
								/>
							</div>
						</div>
					);
				}

				return <small>{val}</small>;
			},
		},
		{
			accessor: 'prezzo_accessori_no_iva',
			id: 'prezzo_accessori_no_iva',
			Header: i18next.t('accessories'),
			width: 250,
			Cell: (data) => {
				return (
					<>
						{data.value === null ? (
							''
						) : (
							<small>
								€{' '}
								{Intl.NumberFormat('it', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								}).format(data.value)}
							</small>
						)}
					</>
				);
			},
		},
		{
			accessor: 'updated_canone',
			id: 'updated_canone',
			Header: i18next.t('rental_fee'),
			width: 5000,
			Cell: (data) => {
				const vatPriced = data.value || 0;
				let percErr = data.row.original.abs_perc_error;

				if (typeof percErr === 'undefined') {
					percErr = null;
				}

				if (percErr === null || percErr >= 10) {
					percErr = 'grey';
				} else if (percErr >= 5 && percErr < 10) {
					percErr = 'orange';
				} else {
					percErr = '#22ff00'; // green
				}
				if (
					data.row.values.noleggiatore ===
					NOLEGGIATORI.ARVAL_PRIVATI.toLowerCase()
				) {
					return (
						<>
							<small>€ {vatPriced.toFixed(2)}</small>
							<small style={{ fontSize: '11px', color: '#a5a5a5' }}>
								&nbsp;(iva inclusa)
							</small>
						</>
					);
				} else if (
					type !== 'ai_offers' &&
					data.row.values.noleggiatore === NOLEGGIATORI.ARVAL_P_IVA.toLowerCase()
				) {
					return (
						<>
							<small>€ {vatPriced.toFixed(2)}</small>
							<small style={{ fontSize: '11px', color: '#a5a5a5' }}>&nbsp;</small>
						</>
					);
				} else {
					return (
						<>
							<small>€ {vatPriced.toFixed(2)}</small>
							<small style={{ fontSize: '11px', color: '#a5a5a5' }}>{` (+iva ${(
								vatPriced * 1.22
							).toFixed(2)})`}</small>
						</>
					);
				}
			},
		},
		{
			accessor: 'durata',
			id: 'durata',
			Header: i18next.t('rental_duration'),
			width: 110,
			Cell: (data) => (
				<>
					{data.value} {i18next.t('months')}
				</>
			),
		},
		{
			accessor: 'km_annui',
			id: 'km_annui',
			Header: i18next.t('annual_km'),
			width: 110,
			Cell: (data) => <>{Intl.NumberFormat('it').format(data.value)}</>,
		},
		{
			accessor: 'anticipo',
			id: 'anticipo',
			Header: i18next.t('downpayment'),
			width: 110,
			Cell: (data) => {
				return (
					<>
						<small>
							€{' '}
							{Intl.NumberFormat('it', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							}).format(data.value)}
						</small>
					</>
				);
			},
		},
		{
			accessor: 'nr_servizi',
			id: 'nr_servizi',
			Header: i18next.t('services'),
			width: 110,
			Cell: (data) => {
				const id = 'services_' + Math.floor(Math.random() * 100000);
				return (
					<>
						<small>{data.value}</small>
						{data.row.original.serviceList?.length ? (
							<span role="button" className="text-muted ms-2">
								<ToolTip
									place="bottom"
									fixed={!inZoom}
									anchorId={id}
									children={
										<>
											<div className="fw-bold">
												Servizi disponibili. In{' '}
												<span className="text-warning">giallo</span> i servizi
												diversi rispetto alla tua richiesta:
											</div>
											<ul className="m-2 ms-0">
												{data.row.original.serviceList.map((s, i) => (
													<li key={i}>{s}</li>
												))}
											</ul>
										</>
									}
								/>
								<FaInfoCircle id={id} />
							</span>
						) : (
							''
						)}
					</>
				);
			},
		},
		{
			accessor: 'updated_commissioni',
			id: 'commissioni',
			Header: i18next.t('commissions'),
			width: 110,
			Cell: ({ row, value }) => {
				const anchorId = `updated-commissioni-info-${row.id}`;
				const isLeasysChiaro = row.original?.noleggiatore === 'leasys';
				return (
					<div>
						<small
							className="me-2"
							style={isLeasysChiaro ? { color: '#db6e0c' } : {}}
						>
							€ {value}
						</small>
						{isLeasysChiaro && (
							<>
								<ToolTip
									anchorId={anchorId}
									fixed={!inZoom}
									content={i18next.t('fixed_commissions')}
								/>
								<FaInfoCircle id={anchorId} />
							</>
						)}
					</div>
				);
			},
		},
		{
			accessor: 'percentage',
			id: 'percentage',
			Header: '%',
			width: 110,
			Cell: (data) => {
				return (
					<>
						{!data.value &&
							data?.row?.original?.noleggiatore.toUpperCase() ===
							NOLEGGIATORI.LEASYS_NOLEGGIO_CHIARO
							? ''
							: data.value}
					</>
				);
			},
		},
		{
			accessor: '',
			id: 'download',
			Header: '',
			width: 60,
			Cell: (data) => {
				const downloadTooltip = isSegnalatore
					? i18next.t('download_pdf_and_create_report')
					: i18next.t('download_pdf');

				const anchorId = `info-download-offer-${data.row.id}`;

				return (
					<Button onClick={() => downloadPdf(data.row)} variant="link">
						<small id={anchorId} className="ms-2">
							{isSegnalatore ? (
								<MdAddCircle
									style={{ width: '1.2rem', height: '1.2rem' }}
									name="delete"
								/>
							) : (
								<Icon
									style={{ width: '1.2rem', height: '1.2rem' }}
									name="download"
								/>
							)}

							<ToolTip
								anchorId={anchorId}
								content={downloadTooltip || i18next.t('download_pdf')}
								className="default"
								fixed={!inZoom}
							/>
						</small>
					</Button>
				);
			},
		},
	];
